import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  HiMenu,
  HiSearch,
  HiPlus,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../components/toaster';
import {
  fetchHinduFestivalList,
  createHinduFestival,
  updateHinduFestival,
  deleteHinduFestival,
  bulkUploadHinduFestivals,
} from '../../services/hinduFestivalService';

const PAGE_SIZE = 20;

const festivalCache = {
  firstLoad: true,
  lastSearch: '',
  data: null,
  totalCount: 0,
  page: 1,
};

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="hf-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="40" stroke="url(#hf-gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" transform="rotate(-90 50 50)" />
      </svg>
    </div>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'}`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

const formatDateRange = (start, end) => {
  if (!start) return '—';
  const fmt = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  if (!end || start === end) return fmt(start);
  return `${fmt(start)} – ${fmt(end)}`;
};

const HinduFestivals = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => festivalCache.lastSearch || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => festivalCache.lastSearch || '');
  const [currentPage, setCurrentPage] = useState(() => festivalCache.page || 1);
  const [data, setData] = useState(() => Array.isArray(festivalCache.data) ? festivalCache.data : []);
  const [totalCount, setTotalCount] = useState(() => festivalCache.totalCount || 0);
  const [isLoading, setIsLoading] = useState(() => festivalCache.firstLoad);
  const [listMutationLoading, setListMutationLoading] = useState(false);
  const [pageChangeLoading, setPageChangeLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ date: '', name: '', importance: '', is_active: true });
  const [formErrors, setFormErrors] = useState({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk upload modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkJson, setBulkJson] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkErrors, setBulkErrors] = useState(null);

  const searchTimerRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const isInitialDebounceRef = useRef(true);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const getPaginationRange = () => {
    if (totalPages === 0) return [];
    const totalBlocks = 7;
    if (totalPages <= totalBlocks) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const left = Math.max(currentPage - 1, 1);
    const right = Math.min(currentPage + 1, totalPages);
    const showLeftDots = left > 2;
    const showRightDots = right < totalPages - 1;
    const pages = [1];
    if (showLeftDots) pages.push('dots');
    for (let p = (showLeftDots ? left : 2); p <= (showRightDots ? right : totalPages - 1); p++) pages.push(p);
    if (showRightDots) pages.push('dots');
    pages.push(totalPages);
    return pages;
  };

  const loadData = useCallback(async (searchText = '', page = 1) => {
    const reqId = ++listRequestIdRef.current;
    if (festivalCache.firstLoad) setIsLoading(true);
    setError(null);
    try {
      const res = await fetchHinduFestivalList(page, PAGE_SIZE, searchText);
      if (reqId !== listRequestIdRef.current) return;
      const items = Array.isArray(res?.data) ? res.data : [];
      const total = res?.totalCount ?? items.length;
      setData(items);
      setTotalCount(total);
      festivalCache.firstLoad = false;
      festivalCache.lastSearch = searchText;
      festivalCache.data = items;
      festivalCache.totalCount = total;
      festivalCache.page = page;
      if (items.length === 0 && page > 1) setCurrentPage(1);
    } catch (err) {
      if (reqId !== listRequestIdRef.current) return;
      setError(err.message || 'Error loading data.');
      setData([]);
      setTotalCount(0);
      festivalCache.firstLoad = false;
    } finally {
      if (reqId === listRequestIdRef.current) {
        setIsLoading(false);
        setPageChangeLoading(false);
        setListMutationLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (isInitialDebounceRef.current) { isInitialDebounceRef.current = false; return; }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => { loadData(debouncedSearch, currentPage); }, [debouncedSearch, currentPage, loadData]);

  const changePage = (next) => {
    setCurrentPage(prev => {
      const n = typeof next === 'function' ? next(prev) : next;
      if (n === prev) return prev;
      queueMicrotask(() => setPageChangeLoading(true));
      return n;
    });
  };

  const openAdd = () => {
    setFormData({ date: '', name: '', importance: '', is_active: true });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
    setShowFormModal(true);
  };

  const openEdit = (item) => {
    setFormData({ date: item.date, name: item.name, importance: item.importance, is_active: item.is_active ?? true });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(item);
    setIsEditing(true);
    setShowFormModal(true);
  };

  const closeForm = () => {
    setShowFormModal(false);
    setFormData({ date: '', name: '', importance: '', is_active: true });
    setFormErrors({});
    setFormGeneralError('');
    setSelectedItem(null);
    setIsEditing(false);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.date.trim()) errs.date = 'Date is required';
    if (!formData.name.trim()) errs.name = 'Festival name is required';
    if (!formData.importance.trim()) errs.importance = 'Importance is required';
    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    setSaving(true);
    setFormErrors({});
    setFormGeneralError('');
    try {
      if (isEditing) {
        const payload = { hindu_festival_id: selectedItem._id, name: formData.name, importance: formData.importance, is_active: formData.is_active };
        if (formData.date !== selectedItem.date) payload.date = formData.date;
        await updateHinduFestival(payload);
        Toast.success('Entry updated successfully');
      } else {
        await createHinduFestival({ date: formData.date, name: formData.name, importance: formData.importance, is_active: formData.is_active });
        Toast.success('Entry created successfully');
      }
      closeForm();
      setListMutationLoading(true);
      await loadData(debouncedSearch, isEditing ? currentPage : 1);
      if (!isEditing) setCurrentPage(1);
    } catch (err) {
      const msg = err.message || 'Failed to save entry';
      if (msg.includes('409') || msg.toLowerCase().includes('already exists')) {
        setFormErrors({ date: 'A festival entry for this date already exists' });
      } else {
        setFormGeneralError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (item) => { setItemToDelete(item); setShowDeleteModal(true); };
  const closeDelete = () => { setShowDeleteModal(false); setItemToDelete(null); };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteHinduFestival(itemToDelete._id);
      Toast.success('Entry deleted successfully');
      setShowDeleteModal(false);
      setItemToDelete(null);
      setListMutationLoading(true);
      await loadData(debouncedSearch, currentPage);
    } catch (err) {
      Toast.error(err.message || 'Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  const openBulkModal = () => { setBulkJson(''); setBulkResult(null); setBulkErrors(null); setShowBulkModal(true); };
  const closeBulkModal = () => { setShowBulkModal(false); setBulkJson(''); setBulkResult(null); setBulkErrors(null); };

  const handleBulkUpload = async () => {
    setBulkErrors(null);
    setBulkResult(null);
    let parsed;
    try {
      parsed = JSON.parse(bulkJson);
      if (!Array.isArray(parsed)) throw new Error('Must be a JSON array');
    } catch {
      setBulkErrors([{ row: '-', date: '-', error: 'Invalid JSON. Must be a valid JSON array.' }]);
      return;
    }
    setBulkUploading(true);
    try {
      const res = await bulkUploadHinduFestivals(parsed);
      const d = res?.data || res;
      setBulkResult(d);
      Toast.success(`Upload complete: ${d.inserted ?? 0} inserted, ${d.updated ?? 0} updated`);
      setListMutationLoading(true);
      await loadData(debouncedSearch, 1);
      setCurrentPage(1);
    } catch (err) {
      try {
        const parsed2 = JSON.parse(err.message);
        const errData = parsed2?.data || parsed2;
        if (Array.isArray(errData?.errors)) {
          setBulkErrors(errData.errors);
        } else {
          setBulkErrors([{ row: '-', date: '-', error: err.message }]);
        }
      } catch {
        setBulkErrors([{ row: '-', date: '-', error: err.message }]);
      }
    } finally {
      setBulkUploading(false);
    }
  };

  const showLoading = isLoading || listMutationLoading || pageChangeLoading;

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors">
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <h1 className="text-xl md:text-3xl font-bold text-gray-800">Hindu Festivals</h1>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
              <div className="w-full md:w-64">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search date, name, importance..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                <button onClick={openBulkModal} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-gray-600 hover:opacity-90 whitespace-nowrap">
                  Bulk Upload
                </button>
                <button onClick={openAdd} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap">
                  <HiPlus className="w-5 h-5" />
                  Add Festival
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {showLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
              <div className="text-center text-gray-500 pointer-events-auto">
                <div className="mb-6">
                  <svg className="w-24 h-24 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No festival entries yet</h3>
                <p className="text-sm text-gray-500 mb-6">Start by adding the first Hindu festival entry</p>
                <button onClick={openAdd} className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-brand hover:opacity-90">
                  <HiPlus className="w-4 h-4" /> Add Festival
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {totalCount > 0 && (
                <div className="px-4 py-2 border-b border-gray-100 flex items-center">
                  <span className="text-xs text-gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
                  </span>
                </div>
              )}

              {error ? (
                <div className="px-4 py-6 text-center text-red-500">{error}</div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-44">Festival Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Importance</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.map(item => (
                        <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-gray-800 font-medium">{item.date}</td>
                          <td className="px-4 py-3 text-gray-800 font-medium">{item.name}</td>
                          <td className="px-4 py-3 text-gray-600">
                            <span title={item.importance}>
                              {item.importance?.length > 80 ? `${item.importance.slice(0, 80)}…` : item.importance}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:opacity-90" style={{ backgroundColor: '#F2E2F7' }} title="Edit">
                                <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                              </button>
                              <button onClick={() => openDelete(item)} className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-colors" title="Delete">
                                <MdDelete className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="px-4 md:px-6 py-4 flex justify-end border-t border-gray-200 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <button onClick={() => changePage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <HiChevronLeft className="w-4 h-4" />
                    </button>
                    {getPaginationRange().map((page, idx) =>
                      page === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">...</span>
                      ) : (
                        <button key={page} onClick={() => changePage(page)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${page === currentPage ? 'bg-brand text-white shadow-sm hover:opacity-90' : 'text-gray-900 hover:bg-gray-100'}`}>
                          {page}
                        </button>
                      )
                    )}
                    <button onClick={() => changePage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <HiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">{isEditing ? 'Edit Hindu Festival' : 'Add Hindu Festival'}</h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {formGeneralError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{formGeneralError}</div>
              )}

              {/* Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.date}
                  onChange={e => { setFormData({ ...formData, date: e.target.value }); if (formErrors.date) setFormErrors({ ...formErrors, date: '' }); }}
                  placeholder='e.g. Wed, Jan 14'
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${formErrors.date ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'}`}
                />
                <p className="mt-1 text-xs text-gray-400">
                  Single day: "Wed, Jan 14". Multi-day range: "Sun, Oct 11 – Mon, Oct 19". Weekdays: Mon Tue Wed Thu Fri Sat Sun. Months: Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec
                </p>
                {isEditing && selectedItem?.start_date && (
                  <p className="mt-1 text-xs text-gray-400">Computed range: {selectedItem.start_date} → {selectedItem.end_date}</p>
                )}
                {formErrors.date && <p className="mt-1 text-xs text-red-600">{formErrors.date}</p>}
              </div>

              {/* Festival Name */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Festival Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => { setFormData({ ...formData, name: e.target.value }); if (formErrors.name) setFormErrors({ ...formErrors, name: '' }); }}
                  placeholder="e.g. Diwali / Deepavali"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${formErrors.name ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'}`}
                />
                {formErrors.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
              </div>

              {/* Importance */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Importance <span className="text-red-500">*</span></label>
                <textarea
                  value={formData.importance}
                  onChange={e => { setFormData({ ...formData, importance: e.target.value }); if (formErrors.importance) setFormErrors({ ...formErrors, importance: '' }); }}
                  placeholder="2–3 sentence description of significance"
                  rows={3}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none ${formErrors.importance ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'}`}
                />
                {formErrors.importance && <p className="mt-1 text-xs text-red-600">{formErrors.importance}</p>}
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.is_active ? 'bg-brand' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button onClick={closeForm} disabled={saving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                {saving ? <><Spinner />{isEditing ? 'Updating...' : 'Adding...'}</> : (isEditing ? 'Update' : 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">Delete Festival</h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">Delete festival entry for <strong>{itemToDelete.name}</strong>? This cannot be undone.</p>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button onClick={closeDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg disabled:opacity-50 flex items-center gap-2">
                {deleting ? <><Spinner />Deleting...</> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Bulk Upload Hindu Festivals</h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-600">Upload a JSON array of festival entries. Existing entries for the same date will be updated (upsert). The backend automatically computes start/end dates from the date string.</p>
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 font-mono whitespace-pre">
                {`[\n  {\n    "date": "Wed, Jan 14",\n    "name": "Makar Sankranti / Pongal",\n    "importance": "Marks the Sun's transition into Capricorn..."\n  },\n  {\n    "date": "Sun, Oct 11 – Mon, Oct 19",\n    "name": "Navaratri",\n    "importance": "A nine-night festival dedicated to Goddess Durga..."\n  }\n]`}
              </div>
              <textarea
                value={bulkJson}
                onChange={e => setBulkJson(e.target.value)}
                placeholder="Paste JSON array here..."
                rows={10}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none font-mono resize-none"
              />
              {bulkResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                  Upload complete: {bulkResult.inserted ?? 0} inserted, {bulkResult.updated ?? 0} updated (total rows: {bulkResult.total_rows ?? '?'})
                </div>
              )}
              {bulkErrors && (
                <div>
                  <p className="text-sm font-semibold text-red-600 mb-2">Validation errors:</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-red-200 rounded-lg">
                      <thead className="bg-red-50">
                        <tr>
                          <th className="text-left px-3 py-2 text-red-700">Row</th>
                          <th className="text-left px-3 py-2 text-red-700">Date</th>
                          <th className="text-left px-3 py-2 text-red-700">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkErrors.map((e, i) => (
                          <tr key={i} className="border-t border-red-100">
                            <td className="px-3 py-2 text-gray-600">{e.row}</td>
                            <td className="px-3 py-2 text-gray-600">{e.date}</td>
                            <td className="px-3 py-2 text-red-600">{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button onClick={closeBulkModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
              <button onClick={handleBulkUpload} disabled={bulkUploading || !bulkJson.trim()} className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                {bulkUploading ? <><Spinner />Uploading...</> : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HinduFestivals;
